import { useOtpChallengeController } from "@/react/contexts/OtpChallengeContext";
import { OTP_HEADERS } from "@/react/hooks/otpChallenge/constants";
import type { ApiError } from "@circle-react/config/CustomErrors";

type MutateAsync<TVars, TData> = (vars: TVars) => Promise<TData>;

interface OtpConfig<TVars> {
  body?: (vars: TVars) => unknown;
  headers?: Record<string, string>;
  method?: "PUT" | "POST" | "PATCH" | "DELETE";
  url?: (vars: TVars) => string; // optional; falls back to error.response.url
}

const isOtpChallengeRequired = (error: ApiError): boolean =>
  error.response?.headers?.get(OTP_HEADERS.challenge) === "true";

const buildOtpRequestConfig = <TVars>(
  vars: TVars,
  cfg: OtpConfig<TVars>,
  error: ApiError,
  otpCode: string,
) => {
  const fallbackUrl = error.response?.url || "";
  const url = cfg.url ? cfg.url(vars) : fallbackUrl;
  const method = cfg.method ?? "PUT";
  const body = cfg.body ? cfg.body(vars) : vars;

  return {
    url,
    method,
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...(cfg.headers || {}),
      [OTP_HEADERS.code]: otpCode,
    },
    body: JSON.stringify(body as Record<string, unknown>),
    credentials: "same-origin" as const,
  };
};

const handleOtpResponse = async (response: Response): Promise<unknown> => {
  if (response.ok) {
    return response.json();
  }

  const text = await response.text();
  const data = text ? (JSON.parse(text) as { message?: string }) : {};

  throw new Error(data.message);
};

export const useWithOtpMutation = <TVars, TData>(
  mutateAsync: MutateAsync<TVars, TData>,
  cfg: OtpConfig<TVars>,
) => {
  const { runWithOtp } = useOtpChallengeController();

  return async (vars: TVars): Promise<TData> => {
    try {
      return await mutateAsync(vars);
    } catch (e) {
      const error = e as ApiError;

      if (!isOtpChallengeRequired(error)) {
        throw e;
      }

      return await runWithOtp<TData>(async otpCode => {
        const requestConfig = buildOtpRequestConfig(vars, cfg, error, otpCode);
        const response = await fetch(requestConfig.url, requestConfig);
        return handleOtpResponse(response) as TData;
      });
    }
  };
};
